"use client"

import { useEffect, useState, useCallback, useRef } from "react"
import dynamic from "next/dynamic"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { MapPin, Calendar, DollarSign, Search, Building2, ChevronLeft, Loader2, ChevronDown } from "lucide-react"
import { stripMarkdown, markdownToHtml } from "@/lib/markdown"
import Link from "next/link"
import { azureDb } from "@/lib/azure/compat-client"
import type { Job } from "@/lib/domain"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { useAuth } from "@/hooks/use-auth"

const PaymentGateway = dynamic(() => import("@/components/payment-gateway"), { ssr: false })
const JOBS_PER_PAGE = 20

export default function JobsPage() {
  const { t, i18n } = useTranslation("jobs")
  const isRTL = i18n.language === "ar"

  const [jobs, setJobs] = useState<any[]>([])
  const [showPayment, setShowPayment] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [subscriptionLoading, setSubscriptionLoading] = useState(true)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("")
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const fetchJobsRef = useRef<((pageNum?: number, search?: string, append?: boolean) => Promise<void>) | null>(null)

  const fetchJobs = useCallback(async (pageNum = 0, search = "", append = false) => {
    try {
      if (pageNum === 0) {
        if (search.trim()) {
          // This is a search operation
          setSearchLoading(true)
        } else {
          // This is initial load
          setLoading(true)
        }
        setError(null)
      } else {
        setLoadingMore(true)
      }

      let query = azureDb
        .from("public_job_listings")
        .select(`
          id,
          company_id,
          title,
          description,
          country,
          job_type,
          salary_min,
          salary_max,
          skills,
          created_at,
          company_name,
          company_logo_blob_key,
          company_location
        `)

      query = query
        .order("created_at", { ascending: false })
        .range(pageNum * JOBS_PER_PAGE, (pageNum + 1) * JOBS_PER_PAGE - 1)

      // Add search filter if provided
      if (search.trim()) {
        query = query.or(`title.ilike.%${search}%,country.ilike.%${search}%,description.ilike.%${search}%`)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        console.error("Error fetching jobs:", fetchError)
        setError(t("error") || "Failed to load jobs. Please try again.")
        toast.error(t("error") || "Failed to load jobs")
        return
      }

      const newJobs = data || []

      if (append) {
        setJobs((prev) => [...prev, ...newJobs])
      } else {
        setJobs(newJobs)
      }

      setHasMore(newJobs.length === JOBS_PER_PAGE)
      setPage(pageNum)
    } catch (error) {
      console.error("Error:", error)
      setError(t("error") || "An unexpected error occurred")
      toast.error(t("error") || "Failed to load jobs")
    } finally {
      setLoading(false)
      setLoadingMore(false)
      setSearchLoading(false)
    }
  }, [t])

  // Update ref when fetchJobs changes
  useEffect(() => {
    fetchJobsRef.current = fetchJobs
  }, [fetchJobs])

  const { initialized, loading: authLoading } = useAuth()

  useEffect(() => {
    const checkSubscriptionAndFetchData = async () => {
      if (!initialized || authLoading) {
        return // Wait for auth to be ready
      }
      setSubscriptionLoading(true);
      const { data: { user } } = await azureDb.auth.getUser();
      if (user) {
        const { data: company } = await azureDb
          .from('companies')
          .select('is_subscribed')
          .eq('user_id', user.id)
          .single();
        if (company) {
          setIsSubscribed(company.is_subscribed);
        }
      }
      setSubscriptionLoading(false);
      if (fetchJobsRef.current) {
        fetchJobsRef.current(0, debouncedSearchTerm)
      }
    };
    checkSubscriptionAndFetchData();
  }, [debouncedSearchTerm, initialized, authLoading])

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value)
    setPage(0)
    setHasMore(true)
  }, [])

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore && fetchJobsRef.current) {
      fetchJobsRef.current(page + 1, debouncedSearchTerm, true)
    }
  }, [page, debouncedSearchTerm, loadingMore, hasMore])

  const filteredJobs = jobs

  if (loading && page === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">{t("loading") || "Loading jobs..."}</p>
        </div>
      </div>
    )
  }

  if (error && jobs.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t("error") || "Error"}</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={() => fetchJobsRef.current?.(0, debouncedSearchTerm)}>{t("tryAgain") || "Try Again"}</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <div className={`container mx-auto px-4 py-8`}>
        {/* Breadcrumb */}
        <div className={`flex items-center space-x-2 text-sm text-gray-600 mb-6 ${isRTL ? 'space-x-reverse' : ''}`}>
          <Link href="/" className="hover:text-blue-600">
            {/* Fix: Use navigation from main translation namespace */}
            {isRTL ? "الرئيسية" : "Home"}
          </Link>
          <span>{">"}</span>
          <span className="text-gray-900">{t("title") || (isRTL ? "تصفح الوظائف" : "Browse Jobs")}</span>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className={`flex items-center space-x-3 mb-8 ${isRTL ? 'space-x-reverse' : ''}`}>
            <Link href="/">
              <Button variant="ghost" size="icon">
                <ChevronLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
              </Button>
            </Link>
            {/* Fix: Use correct translation key */}
            <h1 className="text-3xl font-bold text-gray-900">
              {t("title") || t("browseJobs") || (isRTL ? "تصفح الوظائف" : "Browse Jobs")}
            </h1>
          </div>

          {/* Search */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="relative">
                <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4`} />
                <Input
                  placeholder={t("searchPlaceholder") || (isRTL ? "ابحث عن وظائف، شركات، مهارات، أو مواقع..." : "Search for jobs, companies, skills, or locations...")}
                  className={isRTL ? "pr-10 text-right" : "pl-10"}
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Jobs List */}
          <div className="space-y-6">
            {searchLoading ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
                  <p className="text-gray-600">{t("searching") || (isRTL ? "جاري البحث..." : "Searching...")}</p>
                </CardContent>
              </Card>
            ) : filteredJobs.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t("noJobs") || (isRTL ? "لا توجد وظائف متاحة" : "No jobs available")}
                  </h3>
                  <p className="text-gray-600">
                    {debouncedSearchTerm
                      ? (t("noJobsMessage") || (isRTL ? "لم يتم العثور على وظائف تطابق معايير البحث الخاصة بك." : "No jobs found matching your search criteria."))
                      : (t("noJobs") || (isRTL ? "لا توجد وظائف متاحة" : "No jobs available"))
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {filteredJobs.map((job) => (
                  <JobCard key={job.id} job={job} isRTL={isRTL} />
                ))}

                {/* Load More Button */}
                {hasMore && (
                  <div className="text-center py-8">
                    <Button onClick={loadMore} disabled={loadingMore} variant="outline" size="lg">
                      {loadingMore ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t("loading") || (isRTL ? "جاري التحميل..." : "Loading...")}
                        </>
                      ) : (
                        t("loadMoreJobs") || (isRTL ? "تحميل المزيد من الوظائف" : `Load More Jobs (${jobs.length} shown)`)
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function JobCard({ job, isRTL }: { job: any; isRTL: boolean }) {
  const { t } = useTranslation("jobs")
  const { user } = useAuth()
  const [company, setCompany] = useState<any>(null)
  const [companyLoading, setCompanyLoading] = useState(true)
  const [applying, setApplying] = useState(false)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    const fetchCompany = async () => {
      if (!job.company_id) { setCompanyLoading(false); return }
      try {
        const { data, error } = await azureDb
          .from("public_job_listings")
          .select("company_name, company_logo_blob_key, company_location")
          .eq("id", job.id)
          .single()
        if (!error && data) {
          setCompany({
            company_name: data.company_name,
            logo_url: data.companies?.logo_url,
            location: data.company_location,
          })
        }
      } catch (error) {
        console.error("Error fetching company:", error)
      } finally {
        setCompanyLoading(false)
      }
    }
    if (!job.companies?.company_name) {
      fetchCompany()
    } else {
      setCompany({ company_name: job.companies.company_name, logo_url: job.companies.logo_url })
      setCompanyLoading(false)
    }
  }, [job.company_id, job.companies?.company_name])

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString(isRTL ? "ar-SA" : "en-US", {
      month: "short", day: "numeric", year: "numeric",
    })

  const formatSalary = (min?: number, max?: number) => {
    if (!min && !max) return null
    if (min && max) return `$${min.toLocaleString()} – $${max.toLocaleString()}`
    if (min) return `${isRTL ? "من" : "From"} $${min.toLocaleString()}`
    if (max) return `${isRTL ? "حتى" : "Up to"} $${max.toLocaleString()}`
    return null
  }

  const companyInitial = company?.company_name?.[0]?.toUpperCase() ?? "?"
  const salary = formatSalary(job.salary_min, job.salary_max)
  const preview = stripMarkdown(job.description ?? "")

  const typeLabel: Record<string, string> = {
    "full-time": isRTL ? "دوام كامل" : "Full-time",
    "part-time": isRTL ? "دوام جزئي" : "Part-time",
    contract: isRTL ? "عقد" : "Contract",
    internship: isRTL ? "تدريب" : "Internship",
  }

  return (
    <Card
      className="group relative overflow-hidden hover:shadow-xl transition-all duration-200 border border-gray-200 hover:border-blue-200"
      style={{ direction: isRTL ? "rtl" : "ltr" }}
    >
      {/* Left accent bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-l-lg" />

      <CardContent className="p-6">
        {/* Header row: logo + title + company */}
        <div className={`flex items-start gap-4 mb-4 ${isRTL ? "flex-row-reverse" : ""}`}>
          {/* Logo */}
          <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 border border-gray-200 overflow-hidden">
            {company?.logo_url ? (
              <img src={company.logo_url} alt={company.company_name} className="w-full h-full object-contain p-1" />
            ) : (
              <span className="text-xl font-bold text-gray-400">{companyLoading ? "·" : companyInitial}</span>
            )}
          </div>

          {/* Title + company */}
          <div className={`flex-1 min-w-0 ${isRTL ? "text-right" : "text-left"}`}>
            <h3 className="text-lg font-semibold text-gray-900 leading-tight mb-0.5">
              {job.title}
            </h3>
            <p className="text-sm text-gray-500 font-medium">
              {companyLoading ? "..." : (company?.company_name || (isRTL ? "شركة غير معروفة" : "Unknown Company"))}
            </p>
          </div>
        </div>

        {/* Meta pills */}
        <div className={`flex flex-wrap gap-2 mb-4 ${isRTL ? "flex-row-reverse" : ""}`}>
          {job.job_type && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
              <Calendar className="w-3 h-3" />
              {typeLabel[job.job_type] ?? job.job_type}
            </span>
          )}
          {job.country && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
              <MapPin className="w-3 h-3" />
              {job.country}
            </span>
          )}
          {salary && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100">
              <DollarSign className="w-3 h-3" />
              {salary}
            </span>
          )}
        </div>

        {/* Description preview — visible only when collapsed */}
        {preview && !expanded && (
          <p className="text-sm text-gray-600 leading-relaxed line-clamp-2 mb-3">
            {preview}
          </p>
        )}

        {/* Expandable full details */}
        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-5">
            {job.description && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                  {isRTL ? "وصف الوظيفة" : "Job Description"}
                </p>
                <div
                  className="text-sm text-gray-700 leading-relaxed space-y-1"
                  dangerouslySetInnerHTML={{ __html: markdownToHtml(job.description) }}
                />
              </div>
            )}
            {job.requirements && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                  {isRTL ? "المتطلبات" : "Requirements"}
                </p>
                <div
                  className="text-sm text-gray-700 leading-relaxed space-y-1"
                  dangerouslySetInnerHTML={{ __html: markdownToHtml(job.requirements) }}
                />
              </div>
            )}
          </div>
        )}

        {/* Skills */}
        {job.skills && job.skills.length > 0 && (
          <div className={`flex flex-wrap gap-1.5 mt-4 ${isRTL ? "flex-row-reverse" : ""}`}>
            {job.skills.slice(0, 5).map((skill: string) => (
              <Badge key={skill} variant="secondary" className="text-xs font-normal">
                {skill}
              </Badge>
            ))}
            {job.skills.length > 5 && (
              <Badge variant="outline" className="text-xs font-normal text-gray-400">
                +{job.skills.length - 5}
              </Badge>
            )}
          </div>
        )}

        {/* Footer: expand toggle + date + apply */}
        <div className={`flex items-center justify-between pt-4 mt-4 border-t border-gray-100 ${isRTL ? "flex-row-reverse" : ""}`}>
          <div className={`flex items-center gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
            <span className="text-xs text-gray-400">
              {isRTL ? "نُشر" : "Posted"} {formatDate(job.created_at)}
            </span>
            <button
              onClick={() => setExpanded(!expanded)}
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              {expanded
                ? (isRTL ? "إخفاء التفاصيل" : "Hide details")
                : (isRTL ? "عرض التفاصيل الكاملة" : "View full details")}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
            </button>
          </div>
          <Button
            size="sm"
            onClick={() => { window.location.href = `/applicant/apply?jobId=${job.id}` }}
            disabled={applying}
            className="text-sm px-4"
          >
            {applying ? (
              <><Loader2 className="mr-2 h-3 w-3 animate-spin" />{isRTL ? "جاري..." : "Loading..."}</>
            ) : (
              t("jobCard.applyNow") || t("applyNow") || (isRTL ? "تقدم الآن" : "Apply Now")
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

